import { useEffect, useMemo, useState } from 'react'

// Fallback para crypto.randomUUID
function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback simples
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}
import Toast from '../components/Toast.jsx'
import Modal from '../components/Modal.jsx'
import BackButton from '../components/BackButton.jsx'
import { useAuth } from '../auth/AuthProvider.jsx'
import { getNextOrderNumber } from '../data/db.js'
import { startNewOrder, addItemToOrder, getActiveOrder, listHistoricoPedidos, generateOrderPDF, shareOrderViaWhatsApp, deleteHistoricoPedido, sendOrderToCompras, cancelActiveOrder, listComprasPendentes } from '../data/orders.js'
// NOTIFICAÇÕES DESATIVADAS - import { notificarNovoPedido, notificarPedidoUrgente } from '../data/pushService.js'
import { PRODUCT_TYPES, getProductTypeLabel, listProducts } from '../data/products.js'

function formatDate(ts) {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleDateString('pt-BR')
  } catch {
    return '—'
  }
}

export default function OrdersPage() {
  const { session } = useAuth()
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  const [showNewOrderModal, setShowNewOrderModal] = useState(false)
  const [archivedOrders, setArchivedOrders] = useState([])
  const [products, setProducts] = useState([])
  const [busy, setBusy] = useState(false)
  const [tipo, setTipo] = useState(PRODUCT_TYPES.material)
  const [productQ, setProductQ] = useState('')
  const [selectedProductId, setSelectedProductId] = useState('')
  const [qty, setQty] = useState('1')
  const [obs, setObs] = useState('')
  const [itemAvulso, setItemAvulso] = useState('')
  const [isItemAvulso, setIsItemAvulso] = useState(false)
  const [modalNumero, setModalNumero] = useState('')
  const [carrinho, setCarrinho] = useState([])

  async function reload() {
    const [arquivados, prods, pendentes] = await Promise.all([
      listHistoricoPedidos({ limit: 20, force: true }),
      listProducts({ q: '', tipo: 'todos', status: 'ativo' }),
      listComprasPendentes({ limit: 20, force: true })
    ])
    // Combinar histórico e pendentes, ordenar por data
    const todosPedidos = [
      ...arquivados.map(p => ({ ...p, origem: 'historico' })),
      ...pendentes.map(p => ({ ...p, origem: 'pendente' }))
    ].sort((a, b) => (b.dataEnvio || b.criadoEm || 0) - (a.dataEnvio || a.criadoEm || 0))
    setArchivedOrders(todosPedidos)
    setProducts(prods)
  }

  useEffect(() => {
    reload()
  }, [])

  const filteredProducts = useMemo(() => {
    if (!productQ.trim()) return products.filter(p => p.tipo === tipo && p.status === 'ativo')
    return products.filter(p => {
      const matchTipo = p.tipo === tipo
      const matchBusca = p.nome?.toLowerCase().includes(productQ.toLowerCase()) || 
                        p.codigo?.toLowerCase().includes(productQ.toLowerCase())
      return matchTipo && matchBusca && p.status === 'ativo'
    })
  }, [productQ, products, tipo])

  async function abrirModalNovoPedido() {
    const novoNumero = await getNextOrderNumber()
    setModalNumero(novoNumero)
    setCarrinho([])
    setTipo(PRODUCT_TYPES.material)
    setProductQ('')
    setSelectedProductId('')
    setQty('1')
    setObs('')
    setShowNewOrderModal(true)
  }

  function adicionarAoCarrinho() {
    let produtoNome = ''
    let produtoId = ''
    let tipoItem = tipo

    if (isItemAvulso) {
      // Item avulso (não cadastrado)
      if (!itemAvulso.trim()) {
        setToastType('error')
        setToast('Digite o nome do item.')
        return
      }
      produtoNome = itemAvulso.trim()
      produtoId = `avulso-${generateUUID()}`
    } else {
      // Item da lista de produtos
      if (!selectedProductId) {
        setToastType('error')
        setToast('Selecione um produto.')
        return
      }
      const produto = products.find(p => p.id === selectedProductId)
      if (!produto) return
      produtoNome = produto.nome
      produtoId = selectedProductId
      tipoItem = produto.tipo
    }

    const quantidade = Number(qty)
    if (!quantidade || quantidade <= 0) {
      setToastType('error')
      setToast('Quantidade inválida.')
      return
    }

    const novoItem = {
      id: generateUUID(),
      produtoId: produtoId,
      produtoNome: produtoNome,
      tipo: tipoItem,
      quantidadeSolicitada: quantidade,
      observacao: obs,
      isAvulso: isItemAvulso
    }

    setCarrinho(prev => [...prev, novoItem])
    
    // Limpar campos para próximo item
    setSelectedProductId('')
    setItemAvulso('')
    setQty('1')
    setObs('')
    setToastType('success')
    setToast(`${produtoNome} adicionado ao pedido.`)
  }

  function removerDoCarrinho(itemId) {
    setCarrinho(prev => prev.filter(item => item.id !== itemId))
  }

  async function enviarPedido() {
    if (carrinho.length === 0) {
      setToastType('error')
      setToast('Adicione pelo menos um item ao pedido.')
      return
    }

    setBusy(true)
    try {
      // Limpar pedido ativo anterior (se existir)
      const existente = await getActiveOrder()
      if (existente) {
        await cancelActiveOrder({ usuario: session })
      }

      // Criar pedido ativo
      await startNewOrder()
      
      // Adicionar todos os itens do carrinho
      for (const item of carrinho) {
        await addItemToOrder({
          produtoId: item.produtoId,
          produtoNome: item.produtoNome,
          tipo: item.tipo,
          quantidadeSolicitada: item.quantidadeSolicitada,
          observacao: item.observacao,
          usuario: session
        })
      }

      // Enviar para compras
      const resultado = await sendOrderToCompras({ 
        usuario: session, 
        numeroPedido: modalNumero 
      })

      // NOTIFICAÇÕES PUSH DESATIVADAS
      // const temUrgente = carrinho.some(i => i.urgencia === 'urgente')
      // console.log('[Push] Enviando notificação - urgente:', temUrgente)
      // if (temUrgente) {
      //   await notificarPedidoUrgente({
      //     empresaId: session?.empresaId,
      //     nomeUsuario: session?.usuarioNome || 'Almoxarife',
      //     descricao: carrinho.find(i => i.urgencia === 'urgente')?.produtoNome || 'item urgente'
      //   })
      // } else {
      //   await notificarNovoPedido({
      //     empresaId: session?.empresaId,
      //     nomeUsuario: session?.usuarioNome || 'Almoxarife',
      //     qtdItens: carrinho.length
      //   })
      // }

      setToastType('success')
      setToast(`Pedido ${resultado.compra.numero} enviado para compras.`)
      
      setShowNewOrderModal(false)
      setCarrinho([])
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao enviar pedido.')
    } finally {
      setBusy(false)
    }
  }

  function cancelarPedido() {
    setCarrinho([])
    setShowNewOrderModal(false)
  }

  async function onDownloadPDF(pedido) {
    try {
      await generateOrderPDF(pedido, session?.usuarioNome)
      setToastType('success')
      setToast('PDF gerado.')
    } catch (err) {
      setToastType('error')
      setToast('Falha ao gerar PDF.')
    }
  }

  async function onShareWhatsApp(pedido) {
    try {
      await shareOrderViaWhatsApp(pedido, session?.usuarioNome)
    } catch (err) {
      setToastType('error')
      setToast('Falha ao compartilhar.')
    }
  }

  async function onDeleteOrder(pedido) {
    if (pedido.origem === 'pendente') {
      setToastType('error')
      setToast('Pedidos pendentes não podem ser excluídos. Cancele na tela de Compras.')
      return
    }
    if (!confirm(`Excluir pedido ${pedido.numero}?`)) return
    try {
      await deleteHistoricoPedido(pedido.id)
      setToastType('success')
      setToast('Pedido excluído.')
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao excluir.')
    }
  }

  return (
    <div className="space-y-4">
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />
      <BackButton />

      {/* Header com botão Novo Pedido */}
      <div className="rounded-2xl cm-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Pedidos</div>
            <div className="mt-1 text-xs text-white/70">Histórico de pedidos enviados</div>
          </div>
          <button
            onClick={abrirModalNovoPedido}
            className="rounded-xl bg-accent-600 px-4 py-2 text-sm font-semibold text-white flex items-center gap-2"
          >
            <span>🛒</span>
            <span>Novo Pedido</span>
          </button>
        </div>
      </div>

      {/* Histórico de Pedidos */}
      <div className="space-y-3">
        <div className="text-sm font-semibold text-white/80">Histórico de Pedidos</div>
        {archivedOrders.length === 0 ? (
          <div className="rounded-2xl cm-card p-6 text-center">
            <div className="text-4xl mb-2">📋</div>
            <div className="text-sm text-white/60">Nenhum pedido no histórico.</div>
            <div className="text-xs text-white/40 mt-1">Clique em "Novo Pedido" para começar.</div>
          </div>
        ) : (
          archivedOrders.map((ped) => (
            <div key={ped.id} className="rounded-2xl cm-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="truncate text-sm font-semibold">{ped.numero}</div>
                    {ped.origem === 'pendente' ? (
                      <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-300">Aguardando Compras</span>
                    ) : (
                      <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] text-emerald-300">Finalizado</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-white/70">
                    Data: {formatDate(ped.dataEnvio || ped.dataFinalizacao)} • {ped.itens?.length || 0} itens
                  </div>
                  <div className="mt-1 text-xs text-white/60">
                    Por: {ped.usuarioNome || '—'}
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <button
                  onClick={() => onDownloadPDF(ped)}
                  className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold"
                >
                  PDF
                </button>
                <button
                  onClick={() => onShareWhatsApp(ped)}
                  className="rounded-xl bg-emerald-600/90 px-3 py-2 text-xs font-semibold text-white"
                >
                  WhatsApp
                </button>
                {ped.origem !== 'pendente' && (
                  <button
                    onClick={() => onDeleteOrder(ped)}
                    className="rounded-xl bg-red-600/90 px-3 py-2 text-xs font-semibold text-white"
                  >
                    Excluir
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de Novo Pedido - Carrinho */}
      <Modal
        open={showNewOrderModal}
        title={`Novo Pedido - ${modalNumero}`}
        onClose={cancelarPedido}
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Seleção de Produto */}
          <div>
            <div className="text-xs font-semibold mb-2">Adicionar Item</div>
            
            {/* Tipo */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                type="button"
                onClick={() => setTipo(PRODUCT_TYPES.ferramenta)}
                className={`rounded-xl px-2 py-2 text-xs font-semibold ${tipo === PRODUCT_TYPES.ferramenta ? 'bg-accent-600' : 'bg-white/10'}`}
              >
                Ferramenta
              </button>
              <button
                type="button"
                onClick={() => setTipo(PRODUCT_TYPES.material)}
                className={`rounded-xl px-2 py-2 text-xs font-semibold ${tipo === PRODUCT_TYPES.material ? 'bg-accent-600' : 'bg-white/10'}`}
              >
                Material
              </button>
              <button
                type="button"
                onClick={() => setTipo(PRODUCT_TYPES.epi)}
                className={`rounded-xl px-2 py-2 text-xs font-semibold ${tipo === PRODUCT_TYPES.epi ? 'bg-accent-600' : 'bg-white/10'}`}
              >
                EPI
              </button>
            </div>

            {/* Toggle Item Avulso */}
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="itemAvulso"
                checked={isItemAvulso}
                onChange={(e) => {
                  setIsItemAvulso(e.target.checked)
                  setSelectedProductId('')
                  setItemAvulso('')
                }}
                className="w-4 h-4 rounded border-white/20 bg-white/10"
              />
              <label htmlFor="itemAvulso" className="text-sm text-white/80">
                Item avulso (não cadastrado)
              </label>
            </div>

            {/* Item Avulso - Campo de texto */}
            {isItemAvulso ? (
              <input
                value={itemAvulso}
                onChange={(e) => setItemAvulso(e.target.value)}
                className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none placeholder:text-white/40 mb-2"
                placeholder="Digite o nome do item..."
              />
            ) : (
              <>
                {/* Busca */}
                <input
                  value={productQ}
                  onChange={(e) => setProductQ(e.target.value)}
                  className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none placeholder:text-white/40 mb-2"
                  placeholder="Buscar produto..."
                />

                {/* Select */}
                <select
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm mb-2"
                >
                  <option value="">Selecione um produto...</option>
                  {filteredProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}{p.codigo ? ` (${p.codigo})` : ''}
                    </option>
                  ))}
                </select>
              </>
            )}

            {/* Qtd e Obs */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <input
                type="number"
                inputMode="numeric"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
                placeholder="Qtd"
              />
              <input
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
                placeholder="Observação"
              />
            </div>

            <button
              onClick={adicionarAoCarrinho}
              disabled={!selectedProductId && !isItemAvulso}
              className="w-full rounded-xl bg-white/20 px-4 py-2 text-sm font-semibold disabled:opacity-60"
            >
              + Adicionar ao Carrinho
            </button>
          </div>

          {/* Carrinho */}
          {carrinho.length > 0 && (
            <div className="border-t border-white/10 pt-4">
              <div className="text-xs font-semibold mb-2">Itens do Pedido ({carrinho.length})</div>
              <div className="space-y-2">
                {carrinho.map((item) => (
                  <div key={item.id} className="rounded-xl bg-white/5 p-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{item.produtoNome}</div>
                      <div className="text-xs text-white/70">
                        {getProductTypeLabel(item.tipo)} • Qtd: {item.quantidadeSolicitada}
                        {item.observacao ? ` • ${item.observacao}` : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => removerDoCarrinho(item.id)}
                      className="rounded-xl bg-red-500/20 px-3 py-2 text-xs text-red-300"
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10">
            <button
              onClick={cancelarPedido}
              className="rounded-xl bg-white/10 px-4 py-3 text-sm font-semibold"
            >
              Cancelar
            </button>
            <button
              onClick={enviarPedido}
              disabled={busy || carrinho.length === 0}
              className="rounded-xl bg-accent-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Enviando…' : 'Enviar Pedido'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
